import { useState, useEffect, useCallback, useMemo } from "react";
import { db } from "../../firebase/config";
import {
  collection,
  doc,
  setDoc,
  deleteDoc,
  onSnapshot,
  query,
  orderBy,
  serverTimestamp,
  type Timestamp,
} from "firebase/firestore";
import {
  flagsForNewUpload,
  flagsImportToBordona,
  flagsRemoveFromBordona,
  songImportableToBordona,
  songVisibleInBordona,
  type UserSongAppFields,
} from "../../lib/userSongApps";
import { useStudentAuth } from "./useStudentAuth";

export interface SongEntry {
  id: string;
  title: string;
  artist: string;
  musicXml: string;
  youtubeUrl?: string;
  tempo?: number;
  createdAt: Timestamp;
  inPlay?: boolean;
  inBordona?: boolean;
  createdInApp?: string;
}

type RawSong = SongEntry & UserSongAppFields;

export interface SongInput {
  id?: string;
  title: string;
  artist: string;
  musicXml: string;
  youtubeUrl?: string;
  tempo?: number;
}

export function useSongLibrary() {
  const { user } = useStudentAuth();
  const [allItems, setAllItems] = useState<SongEntry[]>([]);
  const [loading, setLoading] = useState(true);

  const userId = user?.uid;

  useEffect(() => {
    if (!userId) {
      setAllItems([]);
      setLoading(false);
      return;
    }

    const colRef = collection(db, "userSongs", userId, "songs");
    const q = query(colRef, orderBy("createdAt", "desc"));

    const unsub = onSnapshot(q, (snap) => {
      const songs: SongEntry[] = snap.docs.map((d) => ({
        id: d.id,
        ...(d.data() as Omit<SongEntry, "id">),
      }));
      setAllItems(songs);
      setLoading(false);
    });

    return unsub;
  }, [userId]);

  const items = useMemo(
    () => allItems.filter((s) => songVisibleInBordona(s)),
    [allItems]
  );

  const importableItems = useMemo(
    () => allItems.filter((s) => songImportableToBordona(s)),
    [allItems]
  );

  const saveSong = useCallback(
    async (input: SongInput) => {
      if (!userId) return;
      const { id: inputId, ...data } = input;
      const id = inputId || crypto.randomUUID();
      const ref = doc(db, "userSongs", userId, "songs", id);
      const clean = Object.fromEntries(
        Object.entries(data).filter(([, v]) => v !== undefined)
      );
      await setDoc(ref, {
        ...clean,
        ...flagsForNewUpload("bordona"),
        createdAt: serverTimestamp(),
      });
      return id;
    },
    [userId]
  );

  const importToBordona = useCallback(
    async (id: string) => {
      if (!userId) return;
      const ref = doc(db, "userSongs", userId, "songs", id);
      await setDoc(ref, flagsImportToBordona(), { merge: true });
    },
    [userId]
  );

  const removeFromBordona = useCallback(
    async (id: string) => {
      if (!userId) return;
      const ref = doc(db, "userSongs", userId, "songs", id);
      await setDoc(ref, flagsRemoveFromBordona(), { merge: true });
    },
    [userId]
  );

  const deleteSong = useCallback(
    async (id: string) => {
      if (!userId) return;
      await deleteDoc(doc(db, "userSongs", userId, "songs", id));
    },
    [userId]
  );

  return { items, importableItems, loading, saveSong, importToBordona, removeFromBordona, deleteSong };
}
